// src/pages/Motoristas.js

import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Motoristas.css';
import {
    FaUserPlus, FaUsers, FaSearch, FaArrowLeft, FaDownload,
    FaFileExport, FaFileImport, FaIdCard, FaPhone, FaEdit,
    FaTrash, FaEye, FaTimes, FaCheckSquare, FaRegSquare, FaTh, FaList,
    FaCarSide, FaChartLine, FaTrophy, FaCommentDots, FaBullhorn,
    FaSortAlphaDown, FaSortAlphaDownAlt, FaExclamationTriangle
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import MotoristaDetalhesModal from '../components/MotoristaDetalhesModal';
import RankingMotoristas from '../components/RankingMotoristas';
import SuccessModal from './SuccessModal';
import ComunicacaoModal from '../components/ComunicacaoModal';

const getSocketToken = () => {
    return (
        localStorage.getItem('token') ||
        localStorage.getItem('authToken') ||
        localStorage.getItem('jwt') ||
        localStorage.getItem('accessToken') ||
        sessionStorage.getItem('token') ||
        sessionStorage.getItem('authToken') ||
        sessionStorage.getItem('jwt') ||
        sessionStorage.getItem('accessToken') ||
        null
    );
};

export default function Motoristas() {
    const navigate = useNavigate();

    // Estados de Listagem e Dados
    const [motoristas, setMotoristas] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Controle de UI (Visualização e Ranking)
    const [viewMode, setViewMode] = useState('grid');
    const [isRankingOpen, setIsRankingOpen] = useState(false);
    const [selectedDrivers, setSelectedDrivers] = useState([]);

    // Ordenação alfabética (A→Z / Z→A)
    const [sortOrder, setSortOrder] = useState('asc');

    // Zerar base (exclusivo admin_geral)
    const [isZerarModalOpen, setIsZerarModalOpen] = useState(false);
    const [zerarConfirmText, setZerarConfirmText] = useState('');
    const [isZerando, setIsZerando] = useState(false);
    const [zerarResult, setZerarResult] = useState(null);

    // Estados de Formulário e Modais
    const initialFormState = { nome: '', cnh_numero: '', categoria_cnh: '', telefone: '' };
    const [formData, setFormData] = useState(initialFormState);
    const [editingMotorista, setEditingMotorista] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [viewingDriverId, setViewingDriverId] = useState(null);

    // Fluxo: solicitação de atualização de veículo
    const [isForceVehicleModalOpen, setIsForceVehicleModalOpen] = useState(false);
    const [forceVehicleTarget, setForceVehicleTarget] = useState(null);
    const [forceVehicleReason, setForceVehicleReason] = useState('');
    const [isSubmittingForceVehicle, setIsSubmittingForceVehicle] = useState(false);

    // ── Comunicação com motoristas ────────────────────────────────────────────
    // chat individual: comunicacaoTarget = { id, nome, telefone }
    const [comunicacaoTarget, setComunicacaoTarget] = useState(null);

    // broadcast: abre com todos os selectedDrivers mapeados
    const [isBroadcastOpen, setIsBroadcastOpen] = useState(false);

    // ícone pulsante quando mensagem for enviada
    const [communicationPulseIds, setCommunicationPulseIds] = useState({});

    // Utilitários
    const fileInputRef = useRef(null);
    const isFirstSortRender = useRef(true);
    const [isImporting, setIsImporting] = useState(false);
    const [userProfile, setUserProfile] = useState(null);
    const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    // --- BUSCA DE DADOS ---
    const fetchUserData = async () => {
        try {
            const { data } = await api.get('/api/usuarios/me');
            setUserProfile(data.perfil);
        } catch (error) {
            console.error('Erro ao buscar perfil do usuário:', error);
        }
    };

    const fetchMotoristas = async (page = 1) => {
        setIsLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');

            const response = await api.get('/api/drivers', {
                headers: { Authorization: `Bearer ${token}` },
                params: { query: searchQuery, page, limit: 12, order: sortOrder }
            });

            setMotoristas(response.data.data || []);
            setPagination(response.data.pagination || { page: 1, totalPages: 1, total: 0 });
            setSelectedDrivers([]);
        } catch (error) {
            console.error('Erro ao buscar motoristas:', error);
            setError('Falha ao carregar a base de motoristas.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchUserData();
    }, []);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchMotoristas(1);
        }, 500);

        return () => clearTimeout(delayDebounceFn);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery]);

    // Reordena imediatamente ao alternar A→Z / Z→A (sem debounce)
    useEffect(() => {
        if (isFirstSortRender.current) {
            isFirstSortRender.current = false;
            return;
        }
        fetchMotoristas(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortOrder]);

    // ── Socket: pulsa o botão de comunicação quando uma mensagem é enviada ───
    useEffect(() => {
        const token = getSocketToken();

        const socket = io(process.env.REACT_APP_SOCKET_URL || window.location.origin, {
            withCredentials: true,
            auth: { token },
            transports: ['websocket', 'polling']
        });

        const marcarPulsoComunicacao = (motoristaId) => {
            if (!motoristaId) return;

            const id = String(motoristaId);

            setCommunicationPulseIds((prev) => ({
                ...prev,
                [id]: true
            }));

            setTimeout(() => {
                setCommunicationPulseIds((prev) => {
                    const copy = { ...prev };
                    delete copy[id];
                    return copy;
                });
            }, 9000);
        };

        const handleNovaMensagem = (data) => {
            const direcao = data?.mensagem?.direcao;
            const motoristaId = data?.motorista_id;

            // Pulsa somente quando o sistema envia mensagem.
            // Se quiser pulsar também quando o motorista responder, altere para:
            // if (!['enviada', 'recebida'].includes(direcao)) return;
            if (direcao !== 'enviada') return;

            marcarPulsoComunicacao(motoristaId);
        };

        socket.on('connect', () => {
            console.log('[Motoristas/socket] conectado:', socket.id);
        });

        socket.on('connect_error', (err) => {
            console.warn('[Motoristas/socket] erro:', err?.message);
        });

        socket.on('comunicacao:nova_mensagem', handleNovaMensagem);

        return () => {
            socket.off('comunicacao:nova_mensagem', handleNovaMensagem);
            socket.disconnect();
        };
    }, []);

    // --- FUNÇÕES DE INPUT ---
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setEditingMotorista((prev) => ({ ...prev, [name]: value }));
    };

    // --- HANDLERS DE MODAIS ---
    const handleOpenViewModal = (id) => {
        setViewingDriverId(id);
        setIsViewModalOpen(true);
    };

    const handleCloseViewModal = () => {
        setIsViewModalOpen(false);
        setViewingDriverId(null);
    };

    const handleOpenAddModal = () => setIsAddModalOpen(true);

    const handleCloseAddModal = () => {
        setIsAddModalOpen(false);
        setFormData(initialFormState);
    };

    const handleOpenEditModal = (motorista) => {
        setEditingMotorista(motorista);
        setIsEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
        setEditingMotorista(null);
    };

    // --- HANDLERS: SOLICITAÇÃO DE ATUALIZAÇÃO DE VEÍCULO ---
    const handleOpenForceVehicleModal = (motorista) => {
        setForceVehicleTarget(motorista);
        setForceVehicleReason('');
        setIsForceVehicleModalOpen(true);
    };

    const handleCloseForceVehicleModal = () => {
        if (isSubmittingForceVehicle) return;

        setIsForceVehicleModalOpen(false);
        setForceVehicleTarget(null);
        setForceVehicleReason('');
    };

    const handleSubmitForceVehicleRequest = async (e) => {
        e.preventDefault();

        if (!forceVehicleTarget?.id) return;

        setIsSubmittingForceVehicle(true);

        try {
            const response = await api.post(
                `/api/drivers/${forceVehicleTarget.id}/solicitar-troca-veiculo`,
                { motivo: forceVehicleReason.trim() || null }
            );

            setSuccessMessage(
                response.data?.message || 'Solicitação de atualização de veículo enviada com sucesso!'
            );
            setIsSuccessModalVisible(true);
            handleCloseForceVehicleModal();
        } catch (error) {
            const backendErrorMessage = error.response?.data?.error;

            alert(
                backendErrorMessage ||
                'Não foi possível enviar a solicitação de atualização de veículo.'
            );
        } finally {
            setIsSubmittingForceVehicle(false);
        }
    };

    // --- HANDLERS: COMUNICAÇÃO ---
    const getTelMotorista = (driver) => driver.celular || driver.telefone || '';

    const handleOpenComunicacao = (motorista) => {
        const motoristaId = String(motorista.id);

        // Remove pulso ao abrir o chat daquele motorista
        setCommunicationPulseIds((prev) => {
            const copy = { ...prev };
            delete copy[motoristaId];
            return copy;
        });

        setComunicacaoTarget({
            id: motorista.id,
            nome: motorista.nome,
            telefone: getTelMotorista(motorista),
        });
    };

    const handleOpenBroadcast = () => setIsBroadcastOpen(true);

    const handleCloseBroadcast = () => setIsBroadcastOpen(false);

    // Monta a lista de destinatários a partir dos IDs selecionados
    const getMotoristasSelecionados = () =>
        selectedDrivers
            .map((id) => motoristas.find((m) => m.id === id))
            .filter(Boolean)
            .map((m) => ({
                id: m.id,
                nome: m.nome,
                telefone: getTelMotorista(m),
            }));

    // --- LÓGICA DE SELEÇÃO EM MASSA ---
    const handleToggleSelect = (id) => {
        setSelectedDrivers((prev) =>
            prev.includes(id)
                ? prev.filter((driverId) => driverId !== id)
                : [...prev, id]
        );
    };

    const handleSelectAll = () => {
        if (selectedDrivers.length === motoristas.length) {
            setSelectedDrivers([]);
        } else {
            setSelectedDrivers(motoristas.map((m) => m.id));
        }
    };

    const handleMassDelete = async () => {
        if (
            window.confirm(
                `Atenção: Tem certeza que deseja excluir permanentemente ${selectedDrivers.length} registro(s)?`
            )
        ) {
            setIsLoading(true);

            try {
                await Promise.all(selectedDrivers.map((id) => api.delete(`/api/drivers/${id}`)));

                setSuccessMessage(`${selectedDrivers.length} registros removidos com sucesso.`);
                setIsSuccessModalVisible(true);
                fetchMotoristas(1);
            } catch (error) {
                const backendErrorMessage = error.response?.data?.error;

                alert(
                    backendErrorMessage ||
                    'Alguns registros não puderam ser excluídos. Verifique se há usuários do sistema ou Ordens de Serviço ativas na seleção.'
                );

                fetchMotoristas(pagination.page);
            } finally {
                setIsLoading(false);
            }
        }
    };

    // --- OPERAÇÕES CRUD ---
    const handleDelete = async (id) => {
        if (window.confirm('Excluir este registro permanentemente?')) {
            try {
                await api.delete(`/api/drivers/${id}`);

                setSuccessMessage('Excluído com sucesso!');
                setIsSuccessModalVisible(true);

                const isLastItem = motoristas.length === 1 && pagination.page > 1;
                fetchMotoristas(isLastItem ? pagination.page - 1 : pagination.page);
            } catch (error) {
                const backendErrorMessage = error.response?.data?.error;

                alert(
                    backendErrorMessage ||
                    'Erro ao excluir. O motorista pode ter Ordens de Serviço ativas.'
                );
            }
        }
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            await api.post('/api/drivers', formData);

            setSuccessMessage('Motorista cadastrado com sucesso!');
            setIsSuccessModalVisible(true);
            handleCloseAddModal();
            fetchMotoristas(1);
        } catch (error) {
            alert('Erro ao cadastrar. Verifique os dados.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            await api.put(`/api/drivers/${editingMotorista.id}`, editingMotorista);

            setSuccessMessage('Dados atualizados com sucesso!');
            setIsSuccessModalVisible(true);
            handleCloseEditModal();
            fetchMotoristas(pagination.page);
        } catch (error) {
            const backendErrorMessage = error.response?.data?.error;
            alert(backendErrorMessage || 'Erro ao atualizar cadastro.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // --- IMPORT/EXPORT ---
    const handleExport = async (format) => {
        try {
            const response = await api.get(`/api/drivers/export?format=${format}`, {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');

            link.href = url;
            link.setAttribute('download', `relatorio_motoristas.${format}`);

            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            alert('Erro ao exportar dados.');
        }
    };

    const handleDownloadTemplate = (format) => {
        const header = ['nome', 'cnh_numero', 'categoria_cnh', 'telefone'];
        const exampleRow = ['João da Silva', '12345678900', 'A/B', '11987654321'];
        const fileName = `modelo_importacao_motoristas.${format}`;

        if (format === 'csv') {
            const csvContent = [header.join(','), exampleRow.join(',')].join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');

            link.href = url;
            link.setAttribute('download', fileName);

            document.body.appendChild(link);
            link.click();
            link.remove();
        } else if (format === 'xlsx') {
            const worksheet = XLSX.utils.aoa_to_sheet([header, exampleRow]);
            const workbook = XLSX.utils.book_new();

            XLSX.utils.book_append_sheet(workbook, worksheet, 'Motoristas');
            XLSX.writeFile(workbook, fileName);
        }
    };

    const handleImport = async (event) => {
        const file = event.target.files[0];

        if (!file) return;

        const importFormData = new FormData();
        importFormData.append('file', file);

        setIsImporting(true);

        try {
            const response = await api.post('/api/drivers/import', importFormData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setSuccessMessage(response.data.message || 'Dados importados!');
            setIsSuccessModalVisible(true);
            fetchMotoristas(1);
        } catch (error) {
            alert('Falha na importação. Verifique o layout da planilha.');
        } finally {
            setIsImporting(false);

            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handlePageChange = (newPage) => {
        if (
            newPage >= 1 &&
            newPage <= pagination.totalPages &&
            newPage !== pagination.page
        ) {
            fetchMotoristas(newPage);
        }
    };

    const hasAdminPermission = userProfile === 'admin_geral' || userProfile === 'admin';
    const canImportExport = userProfile === 'admin_geral';

    // --- ZERAR BASE (exclusivo admin_geral) ---
    const closeZerarModal = () => {
        if (isZerando) return;
        setIsZerarModalOpen(false);
        setZerarConfirmText('');
        setZerarResult(null);
    };

    const executarZerarBase = async (modo = 'estrito') => {
        if (zerarConfirmText.trim().toUpperCase() !== 'ZERAR') {
            alert('Digite ZERAR para confirmar a operação.');
            return;
        }

        setIsZerando(true);

        try {
            const { data } = await api.post('/api/drivers/zerar-base', {
                confirmar: 'ZERAR',
                modo
            });

            setZerarResult(null);
            setZerarConfirmText('');
            setIsZerarModalOpen(false);
            setSuccessMessage(data.message || 'Base de motoristas zerada.');
            setIsSuccessModalVisible(true);
            fetchMotoristas(1);
        } catch (error) {
            const resp = error.response?.data;

            // Abort controlado: motoristas com vínculo de OS impedem a exclusão total
            if (error.response?.status === 409 && resp?.abortado) {
                setZerarResult(resp);
            } else {
                alert(resp?.error || 'Falha ao zerar a base de motoristas.');
            }
        } finally {
            setIsZerando(false);
        }
    };

    return (
        <main className="mot-page-wrapper">
            {/* CABEÇALHO */}
            <header className="mot-header">
                <div className="mot-header-title-box">
                    <button onClick={() => navigate(-1)} className="mot-btn-back">
                        <FaArrowLeft />
                    </button>

                    <div className="mot-title-layout">
                        <div className="mot-title-row">
                            <h1 className="mot-title">Gestão de Motoristas</h1>

                            {hasAdminPermission && (
                                <button
                                    className="mot-ranking-trigger-pulse"
                                    onClick={() => setIsRankingOpen(true)}
                                >
                                    <FaTrophy />
                                </button>
                            )}
                        </div>

                        <p className="mot-subtitle">
                            Cadastre e analise a performance da sua equipe em tempo real.
                        </p>
                    </div>
                </div>

                <div className="mot-header-actions">
                    {canImportExport && (
                        <div className="mot-actions-secondary">
                            <div className="mot-dropdown">
                                <button type="button" className="mot-btn mot-btn-outline">
                                    <FaDownload /> Modelo
                                </button>

                                <div className="mot-dropdown-menu">
                                    <button type="button" onClick={() => handleDownloadTemplate('csv')}>
                                        .CSV
                                    </button>
                                    <button type="button" onClick={() => handleDownloadTemplate('xlsx')}>
                                        .XLSX
                                    </button>
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={() => fileInputRef.current.click()}
                                className="mot-btn mot-btn-outline"
                                disabled={isImporting}
                            >
                                <FaFileImport /> {isImporting ? 'Importando...' : 'Importar'}
                            </button>

                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleImport}
                                style={{ display: 'none' }}
                                accept=".csv, .xlsx"
                            />

                            <div className="mot-dropdown">
                                <button type="button" className="mot-btn mot-btn-outline">
                                    <FaFileExport /> Exportar
                                </button>

                                <div className="mot-dropdown-menu">
                                    <button type="button" onClick={() => handleExport('csv')}>
                                        .CSV
                                    </button>
                                    <button type="button" onClick={() => handleExport('xlsx')}>
                                        .XLSX
                                    </button>
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={() => setIsZerarModalOpen(true)}
                                className="mot-btn mot-btn-zerar"
                                title="Excluir todos os motoristas cadastrados desta empresa"
                            >
                                <FaExclamationTriangle /> Zerar base
                            </button>
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={handleOpenAddModal}
                        className="mot-btn mot-btn-primary"
                    >
                        <FaUserPlus /> Novo Motorista
                    </button>
                </div>
            </header>

            {/* BARRA DE AÇÕES EM MASSA */}
            {selectedDrivers.length > 0 && hasAdminPermission && (
                <div className="mot-mass-action-bar animate-slide-down">
                    <div className="mot-mass-action-info">
                        <span className="mot-mass-count">{selectedDrivers.length}</span>
                        selecionado(s)
                    </div>

                    <div className="mot-mass-action-buttons">
                        <button
                            onClick={() => setSelectedDrivers([])}
                            className="mot-btn mot-btn-outline"
                        >
                            Cancelar
                        </button>

                        <button
                            onClick={handleOpenBroadcast}
                            className="mot-btn mot-btn-broadcast"
                            title="Enviar comunicado para todos os selecionados"
                        >
                            <FaBullhorn /> Comunicar
                            <span className="mot-broadcast-badge">{selectedDrivers.length}</span>
                        </button>

                        <button onClick={handleMassDelete} className="mot-btn mot-btn-danger">
                            <FaTrash /> Excluir Selecionados
                        </button>
                    </div>
                </div>
            )}

            {/* PESQUISA E CONTROLES DE VISUALIZAÇÃO */}
            <section className="mot-search-section">
                <div className="mot-search-box">
                    <FaSearch className="mot-search-icon" />
                    <input
                        type="text"
                        className="mot-search-input"
                        placeholder="Pesquisar por nome ou CNH..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>

                <div className="mot-search-controls">
                    {hasAdminPermission && motoristas.length > 0 && !isLoading && (
                        <button onClick={handleSelectAll} className="mot-select-all-btn">
                            {selectedDrivers.length === motoristas.length ? (
                                <FaCheckSquare className="text-blue" />
                            ) : (
                                <FaRegSquare />
                            )}
                            Selecionar Todos
                        </button>
                    )}

                    <button
                        type="button"
                        onClick={() => setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))}
                        className="mot-sort-btn"
                        title={sortOrder === 'asc' ? 'Ordem A → Z (clique para inverter)' : 'Ordem Z → A (clique para inverter)'}
                    >
                        {sortOrder === 'asc' ? <FaSortAlphaDown /> : <FaSortAlphaDownAlt />}
                        {sortOrder === 'asc' ? 'A–Z' : 'Z–A'}
                    </button>

                    <div className="mot-view-toggle">
                        <button
                            className={`mot-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                            onClick={() => setViewMode('grid')}
                            title="Visualização em Grid"
                        >
                            <FaTh />
                        </button>

                        <button
                            className={`mot-view-btn ${viewMode === 'list' ? 'active' : ''}`}
                            onClick={() => setViewMode('list')}
                            title="Visualização em Lista"
                        >
                            <FaList />
                        </button>
                    </div>
                </div>
            </section>

            {/* LISTAGEM DE MOTORISTAS */}
            <section className="mot-content">
                {isLoading ? (
                    <div className="mot-state-box">
                        <div className="mot-spinner"></div>
                        <p>Sincronizando equipe...</p>
                    </div>
                ) : error ? (
                    <div className="mot-state-box error">
                        <p>{error}</p>
                    </div>
                ) : motoristas.length === 0 ? (
                    <div className="mot-state-box">
                        <FaUsers className="mot-empty-icon" />
                        <p>Nenhum motorista encontrado.</p>
                    </div>
                ) : (
                    <>
                        {viewMode === 'grid' ? (
                            <div className="mot-grid animate-fade-in">
                                {motoristas.map((motorista) => {
                                    const isSelected = selectedDrivers.includes(motorista.id);
                                    const shouldPulse = communicationPulseIds[String(motorista.id)];

                                    return (
                                        <article
                                            key={motorista.id}
                                            className={`mot-card ${isSelected ? 'selected' : ''}`}
                                        >
                                            <header className="mot-card-header">
                                                <div className="mot-card-title-group">
                                                    {hasAdminPermission && (
                                                        <div
                                                            className="mot-card-checkbox"
                                                            onClick={() => handleToggleSelect(motorista.id)}
                                                        >
                                                            {isSelected ? (
                                                                <FaCheckSquare className="checked" />
                                                            ) : (
                                                                <FaRegSquare />
                                                            )}
                                                        </div>
                                                    )}

                                                    <div className="mot-avatar">
                                                        {motorista.nome?.charAt(0).toUpperCase()}
                                                    </div>

                                                    <h3 title={motorista.nome}>{motorista.nome}</h3>
                                                </div>

                                                <div className="mot-card-actions">
                                                    <button
                                                        onClick={() => handleOpenViewModal(motorista.id)}
                                                        className="mot-icon-btn view"
                                                        title="Performance"
                                                    >
                                                        <FaEye />
                                                    </button>

                                                    <button
                                                        onClick={() => handleOpenComunicacao(motorista)}
                                                        className={`mot-icon-btn communicate ${
                                                            shouldPulse ? 'mot-communication-pulse' : ''
                                                        }`}
                                                        title={
                                                            shouldPulse
                                                                ? 'Mensagem enviada recentemente'
                                                                : 'Enviar mensagem'
                                                        }
                                                    >
                                                        <FaCommentDots />
                                                    </button>

                                                    {hasAdminPermission && (
                                                        <>
                                                            <button
                                                                onClick={() => handleOpenForceVehicleModal(motorista)}
                                                                className="mot-icon-btn vehicle"
                                                                title="Solicitar atualização de veículo"
                                                            >
                                                                <FaCarSide />
                                                            </button>

                                                            <button
                                                                onClick={() => handleOpenEditModal(motorista)}
                                                                className="mot-icon-btn edit"
                                                                title="Editar"
                                                            >
                                                                <FaEdit />
                                                            </button>

                                                            <button
                                                                onClick={() => handleDelete(motorista.id)}
                                                                className="mot-icon-btn delete"
                                                                title="Excluir"
                                                            >
                                                                <FaTrash />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </header>

                                            <div className="mot-card-body">
                                                <div className="mot-info-row">
                                                    <FaIdCard className="mot-info-icon" />
                                                    <span>
                                                        CNH: {motorista.cnh_numero || 'N/A'} ({motorista.categoria_cnh || '-'})
                                                    </span>
                                                </div>

                                                <div className="mot-info-row">
                                                    <FaPhone className="mot-info-icon" />
                                                    <span>{motorista.telefone || 'Sem contato'}</span>
                                                </div>
                                            </div>

                                            <footer className="mot-card-footer">
                                                <button
                                                    className="mot-btn-details-full"
                                                    onClick={() => handleOpenViewModal(motorista.id)}
                                                >
                                                    <FaChartLine /> Painel de Analytics
                                                </button>
                                            </footer>
                                        </article>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="mot-table-wrapper animate-fade-in">
                                <table className="mot-table">
                                    <thead>
                                        <tr>
                                            {hasAdminPermission && <th width="40"></th>}
                                            <th>Motorista</th>
                                            <th>Contato</th>
                                            <th>CNH</th>
                                            <th className="text-right">Ações</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {motoristas.map((motorista) => {
                                            const isSelected = selectedDrivers.includes(motorista.id);
                                            const shouldPulse = communicationPulseIds[String(motorista.id)];

                                            return (
                                                <tr
                                                    key={motorista.id}
                                                    className={isSelected ? 'selected-row' : ''}
                                                >
                                                    {hasAdminPermission && (
                                                        <td
                                                            onClick={() => handleToggleSelect(motorista.id)}
                                                            className="mot-table-checkbox"
                                                        >
                                                            {isSelected ? (
                                                                <FaCheckSquare className="checked" />
                                                            ) : (
                                                                <FaRegSquare />
                                                            )}
                                                        </td>
                                                    )}

                                                    <td>
                                                        <div className="mot-table-info">
                                                            <div className="mot-avatar-small">
                                                                {motorista.nome?.charAt(0).toUpperCase()}
                                                            </div>
                                                            <strong>{motorista.nome}</strong>
                                                        </div>
                                                    </td>

                                                    <td>
                                                        <span className="mot-table-text">
                                                            <FaPhone /> {motorista.telefone || '-'}
                                                        </span>
                                                    </td>

                                                    <td>
                                                        <div className="mot-table-badges">
                                                            <span className="mot-badge">
                                                                <FaIdCard /> {motorista.cnh_numero || 'S/ CNH'}
                                                            </span>
                                                            <span className="mot-badge cat">
                                                                Cat. {motorista.categoria_cnh || '-'}
                                                            </span>
                                                        </div>
                                                    </td>

                                                    <td>
                                                        <div className="mot-table-actions">
                                                            <button
                                                                onClick={() => handleOpenViewModal(motorista.id)}
                                                                className="mot-icon-btn view"
                                                                title="Performance"
                                                            >
                                                                <FaEye />
                                                            </button>

                                                            <button
                                                                onClick={() => handleOpenComunicacao(motorista)}
                                                                className={`mot-icon-btn communicate ${
                                                                    shouldPulse ? 'mot-communication-pulse' : ''
                                                                }`}
                                                                title={
                                                                    shouldPulse
                                                                        ? 'Mensagem enviada recentemente'
                                                                        : 'Enviar mensagem'
                                                                }
                                                            >
                                                                <FaCommentDots />
                                                            </button>

                                                            {hasAdminPermission && (
                                                                <>
                                                                    <button
                                                                        onClick={() => handleOpenForceVehicleModal(motorista)}
                                                                        className="mot-icon-btn vehicle"
                                                                        title="Solicitar atualização de veículo"
                                                                    >
                                                                        <FaCarSide />
                                                                    </button>

                                                                    <button
                                                                        onClick={() => handleOpenEditModal(motorista)}
                                                                        className="mot-icon-btn edit"
                                                                        title="Editar"
                                                                    >
                                                                        <FaEdit />
                                                                    </button>

                                                                    <button
                                                                        onClick={() => handleDelete(motorista.id)}
                                                                        className="mot-icon-btn delete"
                                                                        title="Excluir"
                                                                    >
                                                                        <FaTrash />
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {pagination.total > 0 && (
                            <nav className="mot-pagination">
                                <button
                                    onClick={() => handlePageChange(pagination.page - 1)}
                                    disabled={pagination.page <= 1}
                                >
                                    Anterior
                                </button>

                                <span>
                                    Página <strong>{pagination.page}</strong> de{' '}
                                    <strong>{pagination.totalPages}</strong>
                                </span>

                                <button
                                    onClick={() => handlePageChange(pagination.page + 1)}
                                    disabled={pagination.page >= pagination.totalPages}
                                >
                                    Próxima
                                </button>
                            </nav>
                        )}
                    </>
                )}
            </section>

            {/* MODAL ADICIONAR MOTORISTA */}
            {isAddModalOpen && (
                <div className="mot-modal-overlay" onClick={handleCloseAddModal}>
                    <div className="mot-modal-box" onClick={(e) => e.stopPropagation()}>
                        <header className="mot-modal-header">
                            <h2><FaUserPlus /> Novo Motorista</h2>
                            <button type="button" className="mot-close-btn" onClick={handleCloseAddModal}>
                                <FaTimes />
                            </button>
                        </header>

                        <form onSubmit={handleAddSubmit} className="mot-form">
                            <div className="mot-modal-body">
                                <div className="mot-form-group">
                                    <label>Nome Completo *</label>
                                    <input
                                        type="text"
                                        name="nome"
                                        value={formData.nome}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>

                                <div className="mot-form-row">
                                    <div className="mot-form-group">
                                        <label>Número da CNH</label>
                                        <input
                                            type="text"
                                            name="cnh_numero"
                                            value={formData.cnh_numero}
                                            onChange={handleInputChange}
                                        />
                                    </div>

                                    <div className="mot-form-group">
                                        <label>Categoria *</label>
                                        <select
                                            name="categoria_cnh"
                                            value={formData.categoria_cnh}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="">Selecione...</option>
                                            <option value="A">A</option>
                                            <option value="B">B</option>
                                            <option value="C">C</option>
                                            <option value="D">D</option>
                                            <option value="E">E</option>
                                            <option value="A/B">A/B</option>
                                            <option value="A/C">A/C</option>
                                            <option value="A/D">A/D</option>
                                            <option value="A/E">A/E</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="mot-form-group">
                                    <label>Telefone Celular</label>
                                    <input
                                        type="text"
                                        name="telefone"
                                        value={formData.telefone}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            <footer className="mot-modal-footer">
                                <button
                                    type="button"
                                    className="mot-btn mot-btn-secondary"
                                    onClick={handleCloseAddModal}
                                >
                                    Cancelar
                                </button>

                                <button
                                    type="submit"
                                    className="mot-btn mot-btn-primary"
                                    disabled={isSubmitting}
                                >
                                    Salvar Registro
                                </button>
                            </footer>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL EDITAR MOTORISTA */}
            {isEditModalOpen && editingMotorista && (
                <div className="mot-modal-overlay" onClick={handleCloseEditModal}>
                    <div className="mot-modal-box" onClick={(e) => e.stopPropagation()}>
                        <header className="mot-modal-header">
                            <h2><FaEdit /> Editar Motorista</h2>
                            <button type="button" className="mot-close-btn" onClick={handleCloseEditModal}>
                                <FaTimes />
                            </button>
                        </header>

                        <form onSubmit={handleUpdateSubmit} className="mot-form">
                            <div className="mot-modal-body">
                                <div className="mot-form-group">
                                    <label>Nome Completo *</label>
                                    <input
                                        type="text"
                                        name="nome"
                                        value={editingMotorista.nome}
                                        onChange={handleEditInputChange}
                                        required
                                    />
                                </div>

                                <div className="mot-form-row">
                                    <div className="mot-form-group">
                                        <label>Número da CNH</label>
                                        <input
                                            type="text"
                                            name="cnh_numero"
                                            value={editingMotorista.cnh_numero || ''}
                                            onChange={handleEditInputChange}
                                        />
                                    </div>

                                    <div className="mot-form-group">
                                        <label>Categoria *</label>
                                        <select
                                            name="categoria_cnh"
                                            value={editingMotorista.categoria_cnh || ''}
                                            onChange={handleEditInputChange}
                                            required
                                        >
                                            <option value="A">A</option>
                                            <option value="B">B</option>
                                            <option value="C">C</option>
                                            <option value="D">D</option>
                                            <option value="E">E</option>
                                            <option value="A/B">A/B</option>
                                            <option value="A/C">A/C</option>
                                            <option value="A/D">A/D</option>
                                            <option value="A/E">A/E</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="mot-form-group">
                                    <label>Telefone Celular</label>
                                    <input
                                        type="text"
                                        name="telefone"
                                        value={editingMotorista.telefone || ''}
                                        onChange={handleEditInputChange}
                                    />
                                </div>
                            </div>

                            <footer className="mot-modal-footer">
                                <button
                                    type="button"
                                    className="mot-btn mot-btn-secondary"
                                    onClick={handleCloseEditModal}
                                >
                                    Cancelar
                                </button>

                                <button
                                    type="submit"
                                    className="mot-btn mot-btn-primary"
                                    disabled={isSubmitting}
                                >
                                    Salvar Alterações
                                </button>
                            </footer>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL: SOLICITAR ATUALIZAÇÃO DE VEÍCULO */}
            {isForceVehicleModalOpen && forceVehicleTarget && (
                <div className="mot-modal-overlay" onClick={handleCloseForceVehicleModal}>
                    <div className="mot-modal-box" onClick={(e) => e.stopPropagation()}>
                        <header className="mot-modal-header">
                            <h2><FaCarSide /> Solicitar atualização de veículo</h2>

                            <button
                                type="button"
                                className="mot-close-btn"
                                onClick={handleCloseForceVehicleModal}
                                disabled={isSubmittingForceVehicle}
                            >
                                <FaTimes />
                            </button>
                        </header>

                        <form onSubmit={handleSubmitForceVehicleRequest} className="mot-form">
                            <div className="mot-modal-body">
                                <div className="mot-vehicle-info-box">
                                    <p>
                                        Você está prestes a solicitar que o motorista{' '}
                                        <strong>{forceVehicleTarget.nome}</strong>{' '}
                                        atualize o veículo utilizado no dia pelo aplicativo.
                                    </p>
                                </div>

                                <div className="mot-form-group">
                                    <label>Motivo da solicitação (opcional)</label>
                                    <textarea
                                        value={forceVehicleReason}
                                        onChange={(e) => setForceVehicleReason(e.target.value)}
                                        rows={4}
                                        placeholder="Ex.: Mudança operacional definida pela central, troca de frota, ajuste de veículo em uso..."
                                        className="mot-textarea"
                                        disabled={isSubmittingForceVehicle}
                                    />
                                </div>

                                <div className="mot-vehicle-notice-box">
                                    <FaCarSide className="mot-vehicle-notice-icon" />
                                    <div>
                                        <p className="mot-vehicle-notice-title">
                                            O motorista receberá a solicitação no app
                                        </p>
                                        <p className="mot-vehicle-notice-text">
                                            Ao receber a ação, o aplicativo poderá abrir automaticamente o modal de seleção de veículo.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <footer className="mot-modal-footer">
                                <button
                                    type="button"
                                    className="mot-btn mot-btn-secondary"
                                    onClick={handleCloseForceVehicleModal}
                                    disabled={isSubmittingForceVehicle}
                                >
                                    Cancelar
                                </button>

                                <button
                                    type="submit"
                                    className="mot-btn mot-btn-primary"
                                    disabled={isSubmittingForceVehicle}
                                >
                                    {isSubmittingForceVehicle ? 'Enviando...' : 'Enviar solicitação'}
                                </button>
                            </footer>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL: ZERAR BASE DE MOTORISTAS */}
            {isZerarModalOpen && (
                <div className="mot-modal-overlay" onClick={closeZerarModal}>
                    <div className="mot-modal-box" onClick={(e) => e.stopPropagation()}>
                        <header className="mot-modal-header">
                            <h2><FaExclamationTriangle /> Zerar base de motoristas</h2>
                            <button
                                type="button"
                                className="mot-close-btn"
                                onClick={closeZerarModal}
                                disabled={isZerando}
                            >
                                <FaTimes />
                            </button>
                        </header>

                        <div className="mot-modal-body mot-form">
                            <div className="mot-zerar-warning">
                                <FaExclamationTriangle />
                                <div>
                                    <strong>Ação irreversível.</strong> Isto exclui permanentemente os
                                    motoristas cadastrados desta empresa. Os usuários operacionais do
                                    app (que também aparecem na lista) <strong>não</strong> são afetados.
                                </div>
                            </div>

                            {zerarResult?.abortado && (
                                <div className="mot-zerar-abort">
                                    <div className="mot-zerar-abort-title">
                                        <FaExclamationTriangle /> Bloqueado por vínculos de Ordem de Serviço
                                    </div>

                                    <div className="mot-zerar-abort-stats">
                                        <div className="mot-zerar-abort-stat">
                                            <strong>{zerarResult.total ?? '-'}</strong>
                                            <span>Total</span>
                                        </div>
                                        <div className="mot-zerar-abort-stat">
                                            <strong>{zerarResult.com_vinculo ?? '-'}</strong>
                                            <span>Com OS</span>
                                        </div>
                                        <div className="mot-zerar-abort-stat">
                                            <strong>{zerarResult.sem_vinculo ?? '-'}</strong>
                                            <span>Sem vínculo</span>
                                        </div>
                                    </div>

                                    {zerarResult.dica && (
                                        <p className="mot-zerar-abort-dica">{zerarResult.dica}</p>
                                    )}

                                    {zerarResult.sem_vinculo > 0 && (
                                        <button
                                            type="button"
                                            className="mot-btn-zerar-livres"
                                            onClick={() => executarZerarBase('somente_livres')}
                                            disabled={isZerando}
                                        >
                                            <FaTrash /> Excluir apenas os {zerarResult.sem_vinculo} sem vínculo
                                        </button>
                                    )}
                                </div>
                            )}

                            <div className="mot-form-group">
                                <label>Digite <strong>ZERAR</strong> para confirmar</label>
                                <input
                                    type="text"
                                    value={zerarConfirmText}
                                    onChange={(e) => setZerarConfirmText(e.target.value)}
                                    placeholder="ZERAR"
                                    disabled={isZerando}
                                    autoComplete="off"
                                />
                            </div>
                        </div>

                        <footer className="mot-modal-footer">
                            <button
                                type="button"
                                className="mot-btn mot-btn-secondary"
                                onClick={closeZerarModal}
                                disabled={isZerando}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="mot-btn mot-btn-danger"
                                onClick={() => executarZerarBase('estrito')}
                                disabled={isZerando || zerarConfirmText.trim().toUpperCase() !== 'ZERAR'}
                            >
                                {isZerando ? 'Processando...' : 'Zerar base'}
                            </button>
                        </footer>
                    </div>
                </div>
            )}

            {/* MODAIS COMPLEMENTARES */}
            {isViewModalOpen && (
                <MotoristaDetalhesModal
                    driverId={viewingDriverId}
                    onClose={handleCloseViewModal}
                />
            )}

            {isRankingOpen && (
                <RankingMotoristas
                    data={motoristas}
                    onClose={() => setIsRankingOpen(false)}
                />
            )}

            {isSuccessModalVisible && (
                <SuccessModal
                    message={successMessage}
                    onClose={() => setIsSuccessModalVisible(false)}
                />
            )}

            {/* ── MODAIS DE COMUNICAÇÃO ─────────────────────────────────────── */}

            {/* Chat individual */}
            {comunicacaoTarget && (
                <ComunicacaoModal
                    motoristaId={comunicacaoTarget.id}
                    motoristaNome={comunicacaoTarget.nome}
                    motoristaTel={comunicacaoTarget.telefone}
                    onClose={() => setComunicacaoTarget(null)}
                />
            )}

            {/* Broadcast em massa */}
            {isBroadcastOpen && (
                <ComunicacaoModal
                    motoristas={getMotoristasSelecionados()}
                    onClose={handleCloseBroadcast}
                />
            )}
        </main>
    );
}